import {
    Suspense,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import IA from "@/images/IA.svg";
import { Outlet, useNavigation } from "react-router-dom";
import { useSelector } from "react-redux";
import { NonAdminLeftSidebar } from "@components/Sidebar/NonAdminLeftSidebar";
import { AdminLeftSidebar } from "@components/Sidebar/AdminLeftSidebar";
import { TaleLoadingFallback } from "@components/TaleLoadingFallback";
import LoadingBar from "react-top-loading-bar";
import eventBus from "@/utils/eventBus";
import Header from "./Layout/LayoutHeader";
import AI from "@pages/ai-bot/ai";

export const Layout = ({ overrideRole = null }) => {
    // Get user from Redux instead of localStorage
    const { user } = useSelector((state) => state.auth);

    // Determine role: use override if provided, otherwise get from user
    const role = useMemo(() => {
        if (overrideRole) return overrideRole;
        return user?.tale_role || "normal";
    }, [overrideRole, user]);

    // Check if user is admin
    const isAdminUser = useMemo(() => {
        if (overrideRole === "admin") return true;
        const userType = user?.userType || user?.roles?.[0];
        return userType === "ADMIN";
    }, [overrideRole, user]);

    const [sidebarVisible, setsidebarVisible] = useState(true);
    const [showSidebar, setshowSidebar] = useState(true);
    const [showHeader, setshowHeader] = useState(true);

    const toggleSidebar = useCallback(
        () => setsidebarVisible((x) => !x),
        [setsidebarVisible]
    );

    useEffect(() => {
        eventBus.subscribe("layout-sidebar-visible", (value) =>
            setshowSidebar(value)
        );
        eventBus.subscribe("layout-header-visible", (value) =>
            setshowHeader(value)
        );
        return () => {
            eventBus.unsubscribe("layout-sidebar-visible");
            eventBus.unsubscribe("layout-header-visible");
        };
    }, []);

    const navigation = useNavigation();
    const loadingBarRef = useRef();

    useEffect(() => {
        if (navigation.state === "loading" || navigation.state === "submitting") {
            loadingBarRef.current?.continuousStart();
        }

        if (navigation.state === "idle") {
            loadingBarRef.current?.complete();
        }
    }, [navigation.state]);

    // The sidebars anchor themselves below the header via the --header-h CSS
    // var instead of a guessed pixel offset, so they never overlap the logo
    // regardless of header content/height (loading state, role, i18n string
    // length, etc.).
    const headerWrapperRef = useRef(null);

    useEffect(() => {
        const el = headerWrapperRef.current;
        if (!el) return;

        const setHeaderHeight = () => {
            document.documentElement.style.setProperty(
                "--header-h",
                `${el.getBoundingClientRect().height}px`
            );
        };

        setHeaderHeight();
        const observer = new ResizeObserver(setHeaderHeight);
        observer.observe(el);
        return () => observer.disconnect();
    }, [showHeader]);

    return (
        <>
            <LoadingBar
                ref={loadingBarRef}
                color="#00C0FC"
                shadow={false}
                height={3}
                transitionTime={100}
                waitingTime={300}
            />
            <div
                className={`flex flex-col ${
                    showHeader ? "gap-3" : "gap-0"
                } transition-all px-4`}
            >
                <div
                    ref={headerWrapperRef}
                    className={`${
                        showHeader ? "!max-h-50" : "!max-h-0"
                    } transition-all sticky top-0 pt-4 pb-2 bg-[#EFEFEF]/90 backdrop-blur-lg z-50`}
                >
                    <Header toggleSidebar={toggleSidebar} role={role} />
                </div>
                <div
                    className={`flex ${!showSidebar ? "gap-0" : "gap-5"} transition-all`}
                >
                    <div
                        className={`
                        ${
                            !isAdminUser && !sidebarVisible
                                ? "hidden sm:!flex"
                                : ""
                        }
                        ${
                            showSidebar
                                ? "sm:!max-w-[250px] sm:!min-w-fit"
                                : "opacity-0 invisible -z-[5000] sm:!max-w-0"
                                            }
                            fixed top-10vh left-0 rtl:left-auto rtl:right-0 h-screen z-[60]
                            sm:!relative sm:!top-0 sm:!left-0 rtl:sm:!left-auto sm:!h-auto sm:!z-auto
                            bg-white rounded-2xl
                            transition-all overflow-hidden
                        `}
                    >
                        {isAdminUser ? (
                            <AdminLeftSidebar sidebarVisible={sidebarVisible} />
                        ) : (
                            <NonAdminLeftSidebar
                                sidebarVisible={sidebarVisible}
                                role={role}
                            />
                        )}
                    </div>
                    <div className="main-content transition-all duration-100 grow pb-4 w-full">
                        <Suspense fallback={<TaleLoadingFallback className="py-20" />}>
                            <Outlet />
                        </Suspense>
                    </div>
                </div>
                <div className="fixed right-5 rtl:right-auto rtl:left-5 bottom-5 w-14 h-14 flex justify-center items-center px-1 py-1 rounded-full bg-red-200">
                    <img src={IA} className="w-10" alt="" loading="lazy" />
                </div>
                <AI />
            </div>
        </>
    );
};